import { Box } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { useStageOverview } from '../api/analytics'
import AppointmentsBarChart from '../components/AppointmentsBarChart'
import AppointmentStatusPieChart from '../components/AppointmentStatusPieChart'
import IncompleteProjectsCard from '../components/IncompleteProjectsCard'
import ProjectCountOverview from '../components/ProjectCountOverview'
import { ReportRefreshButton } from '../components/ReportRefreshButton'
import StatusLeadTimeCard from '../components/StatusLeadTimeCard'
import UnscheduledProjectsCard from '../components/UnscheduledProjectsCard'
import WeeklyDispatchedProjects from '../components/WeeklyDispatchedProjects'

import { useAppointmentsQuery } from '@/features/osa-scheduling/api/appointments.queries'

export const AppointmentReportTab: React.FC = () => {
  const queryClient = useQueryClient()
  const {
    data: stageOverviewData,
    isLoading: stageOverviewLoading,
    error: stageOverviewError,
  } = useStageOverview('live')

  const {
    data: appointmentsData,
    isLoading: appointmentsLoading,
    error: appointmentsError,
    refetch: refetchAppointments,
  } = useAppointmentsQuery(
    {
      status: 'dispatched',
      pageSize: stageOverviewData?.dispatched?.all,
      sortField: 'scheduleStartTime',
      sortOrder: 'asc',
      env: 'live',
    },
    {
      enabled: !!stageOverviewData?.dispatched?.all,
    }
  )

  const handleRefresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['analytics'] })
    await refetchAppointments()
  }, [queryClient, refetchAppointments])

  return (
    <>
      <ReportRefreshButton onRefresh={handleRefresh} />
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', alignItems: 'stretch' }}>
        <Box sx={{ maxWidth: 300, display: 'flex' }}>
          <ProjectCountOverview
            data={stageOverviewData}
            isLoading={stageOverviewLoading}
            error={stageOverviewError}
            appointmentsData={appointmentsData?.data}
          />
        </Box>
        <Box sx={{ maxWidth: 350, display: 'flex' }}>
          <UnscheduledProjectsCard
            unscheduledCount={stageOverviewData?.new?.old || 0}
            isLoading={stageOverviewLoading}
            error={stageOverviewError}
          />
        </Box>
        <Box sx={{ maxWidth: 350, display: 'flex' }}>
          <IncompleteProjectsCard
            incompleteCount={stageOverviewData?.dispatched?.old || 0}
            isLoading={stageOverviewLoading}
            error={stageOverviewError}
          />
        </Box>
        <Box sx={{ maxWidth: 500, display: 'flex' }}>
          <AppointmentStatusPieChart />
        </Box>
      </Box>
      <Box sx={{ mt: 3, maxWidth: 800, display: 'flex' }}>
        <StatusLeadTimeCard />
      </Box>
      <Box sx={{ mt: 3 }}>
        <WeeklyDispatchedProjects
          appointmentsData={appointmentsData?.data}
          isLoading={stageOverviewLoading || appointmentsLoading}
          error={stageOverviewError || appointmentsError}
        />
      </Box>
      <Box sx={{ mt: 3 }}>
        <AppointmentsBarChart title="OSAs to be executed" />
      </Box>
      <Box sx={{ mt: 3 }}>
        <AppointmentsBarChart
          timeColumn="confirmationEmailSent"
          title="OSAs dispatched"
          status="dispatched,completed,cancelled"
        />
      </Box>
    </>
  )
}
